import React, {useEffect, useState} from 'react'
import {getResponseFromInterviewId, getResponseFromInterviewIdByDate} from '../_requests'
import * as XLSX from 'xlsx'
import {useAuth} from '../../../modules/auth'
import {Spinner} from 'react-bootstrap'
import {useNavigate, useParams} from 'react-router-dom'
import {isNotEmpty, KTIcon} from '../../../../_metronic/helpers'

const ReportPage: React.FC = () => {
  const navigate = useNavigate()
  const {id, date} = useParams()
  const [interviewName, setInterviewName] = useState('')
  const [loading, setLoading] = useState(true)
  const [answers, setAnswers] = useState([])
  const [tempData, setTempData] = useState([])
  const [countResponse, setCountResponse] = useState(0)
  const {currentUser} = useAuth()

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return

      try {
        let res
        // If date is provided, fetch date-specific responses, otherwise fetch all
        if (date) {
          res = await getResponseFromInterviewIdByDate(id, date)
        } else {
          res = await getResponseFromInterviewId(id)
        }

        if (res && res[0].interviewId) {
          setInterviewName(res[0].interviewId?.interviewName)
          const formattedAnswers = res
          setTempData(formattedAnswers)
          const groupedAnswers = formattedAnswers.map((item) => {
            const groupedQuestions = item?.questions?.reduce((groups, question) => {
              if (question.title?.trim()) {
                if (!groups[question.title]) {
                  groups[question.title] = []
                }
                groups[question.title].push(question)
              } else {
                groups[question?.question] = [question]
              }
              return groups
            }, {})
            return {
              ...item,
              questions: groupedQuestions,
            }
          })
          setAnswers(groupedAnswers)
          setCountResponse(res?.length)
        }
      } catch (error) {
        console.error('Error fetching report data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [id, date])

  const formatDate = (date: Date) => {
    if (!date) return ''
    const date1 = new Date(date)
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    }).format(date1)
    return formattedDate
  }

  const calculateDuration = (startedAt: Date, createdAt: Date) => {
    const duration = new Date(createdAt).getTime() - new Date(startedAt).getTime()
    const minutes = Math.floor(duration / (1000 * 60))
    const seconds = Math.floor((duration % (1000 * 60)) / 1000)
    return `${minutes}m ${seconds}s`
  }

  const handleDownload = () => {
    const jsonToExcel = (data) => {
      // Build header structure with parent-child relationships
      const headerStructure = []
      const columnOrder = ['id']
      const merges = []
      let currentCol = 1 // Start after 'id' column

      // Collect all unique question groups and questions from ALL data entries
      const allQuestionGroups = new Map()

      data.forEach((item) => {
        if (item.questions && typeof item.questions === 'object') {
          Object.entries(item.questions).forEach(([groupTitle, questions]: [string, any[]]) => {
            if (Array.isArray(questions) && questions.length > 0) {
              if (!allQuestionGroups.has(groupTitle)) {
                allQuestionGroups.set(groupTitle, [])
              }

              questions.forEach((question) => {
                const colName =
                  question?.questionId !== '' ? question.questionId : question.question || ''
                const reasonColName = question.reasonId || question.reasonTitle || ''

                // Check if this question already exists in the group
                const existingQuestions = allQuestionGroups.get(groupTitle)
                const questionExists = existingQuestions.some(
                  (q) => q.colName === colName && q.reasonColName === reasonColName
                )

                if (!questionExists && colName) {
                  existingQuestions.push({
                    colName,
                    reasonColName,
                    hasReason: question.reasonResponse?.length > 0 || question.reasonTitle,
                    question,
                  })
                }
              })
            }
          })
        }
      })

      // Build header structure from collected unique questions
      allQuestionGroups.forEach((questions, groupTitle) => {
        if (questions.length > 0) {
          const isParentQuestion = questions.length > 1 || questions[0].question?.title?.trim()

          if (isParentQuestion && questions.length > 1) {
            // Parent question with multiple subquestions
            const startCol = currentCol
            const subColumns = []

            questions.forEach(({colName, reasonColName, hasReason}) => {
              columnOrder.push(colName)
              subColumns.push(colName)
              currentCol++

              // Add reason column if exists
              if (hasReason && reasonColName) {
                columnOrder.push(reasonColName)
                subColumns.push(reasonColName)
                currentCol++
              }
            })

            // Add merge for parent question spanning subquestions
            const endCol = currentCol - 1
            if (endCol > startCol) {
              merges.push({s: {r: 0, c: startCol}, e: {r: 0, c: endCol}})
            }

            headerStructure.push({
              parent: groupTitle,
              children: subColumns,
              startCol,
              endCol,
            })
          } else {
            // Single question without subquestions
            const {colName, reasonColName, hasReason} = questions[0]
            columnOrder.push(colName)

            headerStructure.push({
              parent: groupTitle,
              children: [colName],
              startCol: currentCol,
              endCol: currentCol,
            })
            currentCol++

            // Add reason column if exists
            if (hasReason && reasonColName) {
              columnOrder.push(reasonColName)
              currentCol++
            }
          }
        }
      })

      // Fixed columns at the end
      const fixedEndColumns = {
        lat: 'Latitude',
        lng: 'Longitude',
        duration: 'Duration',
        'Start Time': 'Start Time',
        'End Time': 'End Time',
        'Surveyor ID': 'Surveyor ID',
      }

      Object.keys(fixedEndColumns).forEach((key) => columnOrder.push(key))

      // Build data rows
      const dataForExcel = data.map((item, index) => {
        const questionData = {}

        // Process grouped questions
        if (item.questions && typeof item.questions === 'object') {
          Object.entries(item.questions).forEach(([groupTitle, questions]: [string, any[]]) => {
            if (Array.isArray(questions)) {
              questions.forEach((question) => {
                const columnName =
                  question?.questionId !== '' ? question.questionId : question.question || ''

                if (columnName && (!(columnName in questionData) || !questionData[columnName])) {
                  // Format response based on type
                  if (question.type === 'date' && question.response?.length > 0) {
                    questionData[columnName] = new Date(question.response[0]).toDateString()
                  } else if (question.type === 'time' && question.response?.length > 0) {
                    questionData[columnName] = new Date(question.response[0]).toLocaleString(
                      'en-US',
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      }
                    )
                  } else if (
                    question.coordinates &&
                    question.coordinates.lat &&
                    question.coordinates.lng
                  ) {
                    questionData[columnName] =
                      (question.response?.[0] || '') +
                      ` (lat: ${question.coordinates.lat}, lng: ${question.coordinates.lng})`
                  } else {
                    questionData[columnName] = Array.isArray(question.response)
                      ? question.response.join(', ')
                      : undefined
                  }

                  // Add reason response if available
                  if (question.reasonResponse?.length > 0) {
                    const reasonColumnName = question.reasonId || question.reasonTitle || 'Reason'

                    if (question.reasonType === 'date' && question.reasonResponse[0]?.length > 0) {
                      questionData[reasonColumnName] = new Date(
                        question.reasonResponse[0]
                      ).toDateString()
                    } else if (
                      question.reasonType?.includes('time') &&
                      question.reasonResponse[0]?.length > 0
                    ) {
                      questionData[reasonColumnName] = new Date(
                        question.reasonResponse[0]
                      ).toLocaleString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })
                    } else {
                      questionData[reasonColumnName] = Array.isArray(question.reasonResponse)
                        ? question.reasonResponse.join(', ')
                        : question.reasonResponse
                    }
                  }
                }
              })
            }
          })
        }

        return {
          id: index + 1,
          ...questionData,
          ...(item.geoCoordinates || {}),
          duration: item.startedAt ? calculateDuration(item.startedAt, item.createdAt) : null,
          'Start Time': formatDate(item.startedAt),
          'End Time': formatDate(item.createdAt),
          'Surveyor ID': item.createdBy?.userid,
        }
      })

      // Create worksheet with custom headers
      const worksheet = XLSX.utils.aoa_to_sheet([])

      // Build parent header row (row 0)
      const parentHeaderRow = ['ID']
      headerStructure.forEach((item) => {
        // For single questions, show questionId if available
        if (item.children.length === 1) {
          parentHeaderRow.push(item.children[0])
        } else {
          // For multiple subquestions, show parent title
          parentHeaderRow.push(item.parent)
          // Add empty cells for remaining subquestions
          for (let i = 1; i < item.children.length; i++) {
            parentHeaderRow.push('')
          }
        }
      })
      Object.values(fixedEndColumns).forEach((val) => parentHeaderRow.push(val))

      // Build child header row (row 1)
      const childHeaderRow = ['']
      headerStructure.forEach((item) => {
        // For single questions, leave child row empty (merged vertically)
        if (item.children.length === 1) {
          childHeaderRow.push('')
        } else {
          // For multiple subquestions, show individual question names
          item.children.forEach((child) => childHeaderRow.push(child))
        }
      })
      Object.keys(fixedEndColumns).forEach((key) => childHeaderRow.push(''))

      // Add headers to worksheet
      XLSX.utils.sheet_add_aoa(worksheet, [parentHeaderRow, childHeaderRow], {origin: 'A1'})

      // Add data rows starting from row 2
      XLSX.utils.sheet_add_json(worksheet, dataForExcel, {
        origin: 'A3',
        skipHeader: true,
        header: columnOrder,
      })

      // Apply merges
      worksheet['!merges'] = merges

      // Merge ID cell vertically (rows 0 and 1)
      worksheet['!merges'].push({s: {r: 0, c: 0}, e: {r: 1, c: 0}})

      // Merge fixed end columns vertically
      const fixedEndStartCol = columnOrder.length - Object.keys(fixedEndColumns).length
      Object.keys(fixedEndColumns).forEach((key, idx) => {
        const col = fixedEndStartCol + idx
        worksheet['!merges'].push({s: {r: 0, c: col}, e: {r: 1, c: col}})
      })

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')
      const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'})

      // Convert Excel data to Blob
      const blob = new Blob([excelBuffer], {type: 'application/octet-stream'})
      const tempFileName = interviewName?.toLowerCase()?.replace(/\s/g, '_') || 'data'
      const fileName = `${tempFileName}.xlsx`

      // Create download link
      const downloadLink = document.createElement('a')
      const blobUrl = URL.createObjectURL(blob)
      downloadLink.href = blobUrl
      downloadLink.setAttribute('download', fileName)

      // Trigger download
      downloadLink.click()

      // Clean up the object URL to prevent memory leak
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
    }

    // Create a copy of answers array before reversing to avoid state mutation
    const updatedAnswers = tempData.map((item) => {
      const groupedQuestions = item?.questions?.reduce((groups, question) => {
        const titleId = question.titleId?.trim()
        if (titleId) {
          if (!groups[titleId]) {
            groups[titleId] = []
          }
          groups[titleId].push(question)
        } else {
          groups[question?.question] = [question]
        }
        return groups
      }, {})
      return {
        ...item,
        questions: groupedQuestions,
      }
    })

    jsonToExcel(updatedAnswers)
  }

  const handleEditNavigation = (answerId: string) => {
    // Navigate to edit page, preserving the current context
    if (date) {
      navigate(`/reports/${id}/edit/${answerId}`)
    } else {
      navigate(`edit/${answerId}`)
    }
  }

  return (
    <section>
      <div className={`card card-xl-stretch mb-xl-8`}>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1 capitalize'>{interviewName}</span>
            {date && (
              <span className='text-muted fs-6 fw-semibold'>
                Date:{' '}
                {new Date(date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
          </h3>
          {(currentUser?.profileType === 'admin' || currentUser?.profileType === 'client') && (
            <div className=''>
              <button className='btn btn-primary' onClick={handleDownload}>
                Download Data
              </button>
            </div>
          )}
        </div>
        <div className='card-body py-3'>
          {loading ? (
            <>
              <div className='w-full flex items-center justify-center'>
                <Spinner animation='border' variant='primary' />
              </div>
            </>
          ) : (
            <>
              <h4 className='fw-semibold fs-4'>Total Responses: {countResponse}</h4>
              {answers?.map((item, index) => {
                return (
                  <div className='row gx-10 mb-5 card p-3' key={index}>
                    <div className='col-lg-12'>
                      <div className='text-muted flex justify-between'>
                        <p>#{countResponse - index}</p>
                        <div className='d-flex flex-col gap-2 text-end'>
                          {item?.startedAt &&
                            `
                            Start Time: ${formatDate(item?.startedAt)}, 
                            `}{' '}
                          End Time: {formatDate(item?.createdAt)}
                          {item?.startedAt && (
                            <p className='mb-0'>
                              Duration: {calculateDuration(item?.startedAt, item?.createdAt)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className='font-semibold'>
                        {Object.entries(item?.questions)?.map(([question, elements]: any, idx) => {
                          return (
                            <div className='my-2' key={idx}>
                              <p className=''>
                                {idx + 1}) {question}
                              </p>
                              {elements?.map((elem, elemIdx) => (
                                <div key={elemIdx}>
                                  {elements?.length > 1 && (
                                    <p className='ms-4 mt-4'>
                                      {`${idx + 1}.${elemIdx + 1})`} {elem?.question}
                                    </p>
                                  )}
                                  {elem?.type === 'date' && elem.response?.[0]?.length > 0 ? (
                                    <span className='text-primary ms-4'>
                                      {new Date(elem.response[0]).toDateString()}
                                    </span>
                                  ) : elem?.type?.includes('time') &&
                                    elem.response?.[0]?.length > 0 ? (
                                    <span className='text-primary ms-4'>
                                      {new Date(elem.response[0]).toLocaleString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true,
                                      })}
                                    </span>
                                  ) : (
                                    <span className='ms-4'>
                                      {elem.response.map((currElem, i) => (
                                        <span className='text-primary' key={i}>
                                          {currElem}{' '}
                                        </span>
                                      ))}
                                    </span>
                                  )}
                                  {elem?.coordinates && (
                                    <p className='text-muted ms-4'>
                                      lat: {elem?.coordinates.lat || 'N/A'}, lng:{' '}
                                      {elem?.coordinates.lng || 'N/A'}
                                    </p>
                                  )}
                                  {elem?.comment !== '' && (
                                    <p className='text-muted ms-4'>Comment: {elem.comment}</p>
                                  )}
                                  {elem?.reasonResponse?.length > 0 && (
                                    <p className='text-muted ms-4'>
                                      {elem.reasonTitle || 'Reason'}:
                                      {elem?.reasonType === 'date' &&
                                      elem.reasonResponse?.[0]?.length > 0 ? (
                                        <span className='text-primary ms-4'>
                                          {new Date(elem.reasonResponse[0]).toDateString()}
                                        </span>
                                      ) : elem?.reasonType?.includes('time') &&
                                        elem.reasonResponse?.[0]?.length > 0 ? (
                                        <span className='text-primary ms-4'>
                                          {new Date(elem.reasonResponse[0]).toLocaleString(
                                            'en-US',
                                            {
                                              hour: '2-digit',
                                              minute: '2-digit',
                                              hour12: true,
                                            }
                                          )}
                                        </span>
                                      ) : (
                                        <span className='ms-4'>
                                          {elem.reasonResponse?.join(', ')}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )
                        })}
                      </div>
                      <div className='text-muted flex justify-between mt-3 align-items-center'>
                        {currentUser?.profileType === 'admin' ? (
                          <button
                            type='button'
                            className='btn btn-sm btn-warning'
                            onClick={() => handleEditNavigation(item?._id)}
                          >
                            <KTIcon iconName='pencil' className='fs-5' />
                            Edit
                          </button>
                        ) : (
                          <div></div>
                        )}
                        <div className='flex flex-col justify-end items-end gap-1'>
                          {item?.createdBy?.userid && (
                            <p className='mb-0'>Surveyor ID: {item?.createdBy?.userid}</p>
                          )}
                          <p>
                            lat: {item.geoCoordinates.lat}, lng: {item.geoCoordinates.lng}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default ReportPage
